import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { Grid, Typography } from "@material-ui/core";

import EnergeaLoadingIcon from "../components/EnergeaLoadingIcon";

const LoadingContext = createContext(null);

export const useLoading = () => {
  const context = useContext(LoadingContext);
  if (!context) {
    throw new Error("useLoading must be used within a LoadingProvider");
  }
  return context;
};

export const LoadingProvider = ({ children }) => {
  const [loadingCounts, setLoadingCounts] = useState(() => new Map());
  const [loadingMessage, setLoadingMessage] = useState("");

  const showLoading = useCallback((id = "default", message = "") => {
    const normalizedId = id || "default";
    setLoadingCounts((prev) => {
      const next = new Map(prev);
      const current = next.get(normalizedId) || 0;
      next.set(normalizedId, current + 1);
      return next;
    });

    if (typeof message === "string" && message.trim()) {
      setLoadingMessage(message);
    }
  }, []);

  const hideLoading = useCallback((id = "default") => {
    const normalizedId = id || "default";
    setLoadingCounts((prev) => {
      const current = prev.get(normalizedId);
      if (!current) {
        return prev;
      }

      const next = new Map(prev);
      if (current <= 1) {
        next.delete(normalizedId);
      } else {
        next.set(normalizedId, current - 1);
      }
      return next;
    });
  }, []);

  const isLoading = loadingCounts.size > 0;

  useEffect(() => {
    if (!isLoading && loadingMessage) {
      setLoadingMessage("");
    }
  }, [isLoading, loadingMessage]);

  const value = useMemo(
    () => ({
      isLoading,
      loadingMessage,
      showLoading,
      hideLoading,
      setLoadingMessage,
    }),
    [hideLoading, isLoading, loadingMessage, showLoading]
  );

  return (
    <LoadingContext.Provider value={value}>
      {children}
      <Grid
        container
        justifyContent="center"
        alignItems="center"
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          width: "100vw",
          height: "100vh",
          zIndex: 9999,
          backgroundColor: "rgba(255, 255, 255, 0.8)",
          backdropFilter: "blur(4px)",
          WebkitBackdropFilter: "blur(4px)",
          opacity: isLoading ? 1 : 0,
          visibility: isLoading ? "visible" : "hidden",
          pointerEvents: isLoading ? "auto" : "none",
          transition: "opacity 0.12s ease, visibility 0.12s ease",
        }}
      >
        <Grid item style={{ textAlign: "center" }}>
          <EnergeaLoadingIcon
            size={88}
            paused={!isLoading}
            aria-label="Carregando"
          />
          {loadingMessage ? (
            <Typography
              variant="body2"
              style={{
                marginTop: "0.9rem",
                color: "rgba(1, 54, 46, 0.8)",
                fontWeight: 700,
                textAlign: "center",
              }}
            >
              {loadingMessage}
            </Typography>
          ) : null}
        </Grid>
      </Grid>
    </LoadingContext.Provider>
  );
};

export default LoadingContext;
